import PropTypes from 'prop-types'
import { cn } from '@/lib/utils'
import React from 'react'
import { HelpCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip'

const InputWithIcon = React.forwardRef(
  (
    {
      icon,
      iconPosition = 'left',
      label,
      tooltipHead,
      tooltipBody,
      className,
      type,
      helpText,
      fieldDescription,
      hasError = false,
      ...props
    },
    ref
  ) => {
    const handleInputClick = e => {
      // Ensure the input gets focus when clicked
      e.target.focus()
    }

    return (
      <div className="flex flex-col items-start self-stretch gap-1.5" data-testid="input-with-icon">
        <p className="text-sm font-medium text-[--gray-700]">{label}</p>
        <div
          className={cn('flex min-h-10 items-center rounded-md border bg-white pl-3 text-base w-full', className)}
          style={{
            boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.05)',
            border: hasError ? '1px solid var(--Error-300, #FDA29B)' : '1px solid var(--Gray-300, #D0D5DD)',
          }}
        >
          {iconPosition === 'left' && icon}
          <input
            ref={ref}
            type={type}
            className={`w-full focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-[#667085] placeholder:font-light`}
            style={{
              textOverflow: 'ellipsis',
              direction: 'ltr',
              width: '100%',
              color: 'var(--Gray-900, #101828)',
              fontWeight: '300',
            }}
            onClick={handleInputClick}
            {...props}
          />
          <div className="px-4 flex items-center gap-2">
            {iconPosition === 'right' && icon}
            {tooltipHead || tooltipBody ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle
                      className="w-4 h-4 cursor-pointer"
                      color="#98A2B3"
                      name="help-icon"
                      data-testid="help-icon"
                    />
                  </TooltipTrigger>
                  <TooltipContent className="mb-1 flex flex-col font-sans bg-[#101828] p-3 rounded-md max-w-80 gap-1">
                    <p className="text-white text-xs font-[600]">{tooltipHead}</p>
                    <p className="text-[#D0D5DD] text-xs font-[500]">{tooltipBody}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </div>
        </div>
        <p className="text-[#475467] text-sm font-[400] mb-">{fieldDescription}</p>
        {helpText}
      </div>
    )
  }
)

InputWithIcon.displayName = 'InputWithIcon'

export { InputWithIcon }

InputWithIcon.propTypes = {
  icon: PropTypes.node,
  type: PropTypes.string,
  helpText: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  tooltipHead: PropTypes.string,
  tooltipBody: PropTypes.string,
  fieldDescription: PropTypes.string,
  iconPosition: PropTypes.string,
  hasError: PropTypes.bool,
}
